/**
 * 评级系统定义 (Rank System)
 *
 * 统一管理游戏内的 S-F 评级及其对应的数值权重/范围。
 * 供技能系统、属性分配系统使用。
 */

import { z } from 'zod';

// ============================================
// 1. 评级定义
// ============================================

export const RankSchema = z.enum(['S', 'A', 'B', 'C', 'D', 'E', 'F']);
export type Rank = z.infer<typeof RankSchema>;
export const RANKS: Rank[] = ['S', 'A', 'B', 'C', 'D', 'E', 'F'];
export const DEFAULT_RANK: Rank = 'C';
export const DEFAULT_COMBAT_TIER_RANK: Rank = DEFAULT_RANK;
export const DEFAULT_SKILL_TIER_RANK: Rank = 'E';
export const DEFAULT_SKILL_MASTERY_RANK: Rank = 'F';

// ============================================
// 1.1 难度映射（统一来源）
// ============================================

/**
 * 平滑难度曲线：内部统一 1~21 档（越大越难）
 * 档位中心点按 S~F 等距分布，后续可叠加轻微随机扰动。
 */
export const RANK_TO_DIFFICULTY_LEVEL_21: Record<Rank, number> = {
  S: 20,
  A: 17,
  B: 14,
  C: 11,
  D: 8,
  E: 5,
  F: 2,
};

export const DIFFICULTY_LEVEL_21_TO_RANK_RANGES: Array<{ min: number; max: number; rank: Rank }> = [
  { min: 1, max: 3, rank: 'F' },
  { min: 4, max: 6, rank: 'E' },
  { min: 7, max: 9, rank: 'D' },
  { min: 10, max: 12, rank: 'C' },
  { min: 13, max: 15, rank: 'B' },
  { min: 16, max: 18, rank: 'A' },
  { min: 19, max: 21, rank: 'S' },
];

/**
 * 属性技能的等级范围 (参考 skillGenerator.ts)
 * 将 Tier 转换为具体的属性 Level
 */
export const ATTRIBUTE_TIER_RANGES: Record<Rank, [number, number]> = {
  F: [1, 5],
  E: [6, 15],
  D: [16, 30],
  C: [31, 50],
  B: [51, 70],
  A: [71, 90],
  S: [91, 100],
};

/**
 * 技能 AP 消耗映射
 */
export const SKILL_AP_COST: Record<Rank, number> = {
  F: 1,
  E: 1,
  D: 2,
  C: 2,
  B: 3,
  A: 4,
  S: 5,
};

/**
 * 技能升级经验倍率
 */
export const SKILL_EXP_MULTIPLIERS: Record<Rank, number> = {
  F: 1.0,
  E: 1.1,
  D: 1.25,
  C: 1.5,
  B: 1.9,
  A: 2.5,
  S: 3.5,
};

/**
 * 战斗中技能品阶伤害倍率
 */
export const SKILL_DAMAGE_MULTIPLIERS: Record<Rank, number> = {
  F: 0.1,
  E: 0.5,
  D: 0.8,
  C: 1.0,
  B: 1.2,
  A: 1.8,
  S: 2.5,
};

// ============================================
// 2. 辅助函数
// ============================================

/**
 * 获取随机范围内的一个整数
 */
export function randomInRange(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * 判断字符串是否为合法 Rank
 */
export function isRank(value: string): value is Rank {
  return (RANKS as string[]).includes(value);
}

/**
 * 将任意字符串归一化为 Rank
 */
export function toRank(value: string | undefined, fallback: Rank = DEFAULT_RANK): Rank {
  if (value && isRank(value)) {
    return value;
  }
  return fallback;
}

/**
 * 将任意输入归一化为 Rank（支持数字难度 1-21）
 */
export function normalizeRank(value: string | number | undefined, fallback: Rank = DEFAULT_RANK): Rank {
  if (typeof value === 'string') {
    return toRank(value.trim().toUpperCase(), fallback);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return difficultyLevelToRank(value);
  }
  return fallback;
}

/**
 * Rank 映射为内部难度等级（1-21）。
 * options.jitter=true 时会加入轻微随机扰动（±1，且 0 偏置更高）。
 */
export function rankToDifficultyLevel(rank: Rank, options?: { jitter?: boolean; random?: () => number }): number {
  const base = RANK_TO_DIFFICULTY_LEVEL_21[rank];
  if (!options?.jitter) return base;

  const random = options.random || Math.random;
  const roll = random();
  const offset = roll < 0.2 ? -1 : roll > 0.8 ? 1 : 0;
  return Math.max(1, Math.min(21, base + offset));
}

/**
 * 内部难度等级（1-21）映射为 Rank
 */
export function difficultyLevelToRank(level: number): Rank {
  const normalized = Math.max(1, Math.min(21, Math.round(level)));
  const range = DIFFICULTY_LEVEL_21_TO_RANK_RANGES.find(item => normalized >= item.min && normalized <= item.max);
  return range?.rank || DEFAULT_RANK;
}

/**
 * 根据评级获取属性值
 */
export function getAttributeValueByRank(rank: Rank): number {
  const range = ATTRIBUTE_TIER_RANGES[rank];
  return randomInRange(range[0], range[1]);
}

/**
 * 根据评级获取技能 AP 消耗
 */
export function getSkillApCostByRank(rank: Rank): number {
  return SKILL_AP_COST[rank];
}

/**
 * 根据评级获取技能经验倍率
 */
export function getSkillExpMultiplierByRank(rank: Rank): number {
  return SKILL_EXP_MULTIPLIERS[rank];
}

/**
 * 根据评级获取技能伤害倍率
 */
export function getSkillDamageMultiplierByRank(rank: Rank): number {
  return SKILL_DAMAGE_MULTIPLIERS[rank];
}
